import { useMemo } from 'react';
import { useCareTeamMembers } from '../../../api/careTeam/useCareTeamMembers';
import { useAuth } from '../../../hooks';

type CareTeamMemberRecord = {
  id: string;
  display_name: string;
  /**
   * Prototype-only. The real `GET /private/care-team-member/` returns a flat
   * list with no role, so this comes from the fixtures rather than the API.
   * Unroled members are treated as providers so nobody disappears from the
   * picklists if this lands server-side without a value yet.
   */
  type?: 'provider' | 'np' | 'rd' | 'pharmacist';
};

// Providers, then NPs, then RDs, then pharmacists — the order a care team
// member would scan. Unroled members sort with the providers.
const ROLE_ORDER: Record<string, number> = { provider: 0, np: 1, rd: 2, pharmacist: 3 };

/**
 * Care-team picklist for the roster sidebar: Providers. Coach has its own
 * hook (`useDashboardCoach`) since it matches on id rather than name.
 *
 * As of round 8 this is every clinician regardless of role — Providers, NPs,
 * Registered Dietitians, and Pharmacists all in one searchable list, per team
 * feedback asking to search RDs/pharmacists from the same field rather than
 * hunting through their own now-removed dropdowns. NPs already shared the
 * provider pool from earlier in round 8 (same patient-side field,
 * `provider_first_name`/`provider_last_name` — a patient has one primary
 * provider, who happens to be an MD, DO, or NP); RDs and pharmacists don't
 * share a field with anyone, but they're folded into the same *dropdown*
 * regardless, since `matchesCareTeam` now checks a member's assignment across
 * all three fields for a hit rather than just the provider one — see its doc
 * comment in filters.ts for why this doesn't create an accidental AND the way
 * flattening roles did pre-round-6.
 *
 * Options carry `display_name` as the value, not the member id: the roster
 * stores providers/RDs/pharmacists as name pairs and `next_meeting` embeds a
 * display name, so names are the only join available today.
 */
const useCareTeamMember = () => {
  const { user: authenticatedProvider } = useAuth();
  const { careTeamMemberData } = useCareTeamMembers();

  const members = (careTeamMemberData ?? []) as CareTeamMemberRecord[];

  const providerOptions = useMemo(
    () =>
      [...members]
        .sort(
          (a, b) =>
            (ROLE_ORDER[a.type ?? 'provider'] ?? 0) -
            (ROLE_ORDER[b.type ?? 'provider'] ?? 0)
        )
        .map((m) => ({ label: m.display_name, value: m.display_name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [careTeamMemberData]
  );

  // Only seeds a default when the authenticated user is themselves a care-team
  // member — a coach or an admin signing in should not land on someone else's
  // filtered panel.
  const initialSelectedProviderName = members.find(
    (m) => m.id === authenticatedProvider?.id
  )?.display_name;

  return {
    initialSelectedProviderName,
    providerOptions,
  };
};

export default useCareTeamMember;
